<template>
  <header
    class="master-top-bar"
    :class="{ 'master-top-bar--search-active': showSearchResults }"
    :style="brandingVars"
    v-if="!isLoading">
    <div
      class="master-top-bar__logo"
      :class="{ 'master-top-bar__logo--collapsed': showSearchResults }"
      @click="handleLogoClick"
      @keydown.enter="handleLogoClick"
      tabindex="0"
      role="button">
      <VcImage v-if="logo" :image="logo" :alt="logoAlt" />
    </div>

    <div
      class="master-top-bar__navigation"
      :class="{ 'master-top-bar__navigation--collapsed': showSearchResults }">
      <TopBarNavigation
        :items="topLevelItems"
        :active-item-uid="activeTopBarItemUid"
        @item-click="navItemClick"/>
    </div>

    <div
      class="master-top-bar__actions"
      :class="{ 'master-top-bar__actions--search-active': showSearchResults }">
      <TopBarActions
        :is-search-active="showSearchResults"
        data-qa="MasterTopBar-actions"/>
    </div>
  </header>

  <div v-else class="master-top-bar master-top-bar--loading" :style="brandingVars">
    <VcSkeleton type="text" width="120px" height="32px" />
    <VcSkeleton type="text" width="300px" height="32px" />
    <VcSkeleton type="text" width="200px" height="32px" />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import handleNavigation from '@/utils/navigationUtils';
import outgoingPostMessageUtils from '@/utils/eventCommunicationUtils/outgoingPostMessageUtils';
import { hasAction, executeAction } from '../utils/actionExecutor';
import TopBarNavigation from './TopBarNavigation.vue';
import TopBarActions from './TopBarActions.vue';

export default {
  name: 'MasterTopBar',
  components: {
    TopBarNavigation,
    TopBarActions,
  },
  props: {
    logoAlt: {
      type: String,
      default: 'Logo',
    },
    defaultRoute: {
      type: String,
      default: '/dashboard',
    },
  },
  computed: {
    ...mapGetters('AuthStore', ['staffUid']),
    ...mapGetters('SearchBarStore', ['showSearchResults', 'isWaitingForSearchResponse']),
    ...mapGetters('AngularStore', ['isAngularLoaded']),
    ...mapGetters('PlatformNavigationStore', [
      'topLevelItems',
      'isLoading',
      'hasFatalError',
      'rootItem',
      'activeTopBarItemUid',
      'navigationTree',
      'showSidebar',
    ]),
    ...mapGetters('BusinessStore', [
      'logo',
      'primaryColor',
      'attributes',
      'directorySettings',
      'isPartnerDirectory',
      'hasHelpLinks',
      'businessId',
    ]),
    brandingVars() {
      return {
        '--primary-color': this.primaryColor,
      };
    },
  },
  watch: {
    $route: {
      handler(to) {
        if (this.navigationTree && this.navigationTree.length > 0) {
          this.setActiveItemFromRoute({ path: to.path });
        }
      },
      immediate: true,
      deep: true,
    },
    navigationTree: {
      handler(tree) {
        if (tree && tree.length > 0) {
          this.setActiveItemFromRoute({ path: this.$route.path });
        }
      },
      immediate: true,
    },
    hasFatalError(hasError) {
      if (hasError) {
        this.handleFatalError();
      }
    },
    isAngularLoaded(loaded) {
      if (loaded) {
        this.replaySidebarState();
      }
    },
    showSearchResults(value) {
      this.setIsOverlayDisplay(value);
    },
  },
  async created() {
    this.addI18nModule('notifications-pov');
    this.addI18nModule('layout-pov');
    this.initNotificationCenterBadge({ staffUid: this.staffUid });
    this.$router.beforeEach(this.routeChange);
    try {
      await this.fetchNavigation();
    } catch (error) {
      console.error('Failed to load navigation:', error);
    }
  },
  methods: {
    ...mapActions('PlatformNavigationStore', {
      storeHandleItemClick: 'handleItemClick',
      fetchNavigation: 'fetchNavigation',
      setActiveItemFromRoute: 'setActiveItemFromRoute',
      activateDefaultItem: 'activateDefaultItem',
    }),
    ...mapActions('SearchBarStore', ['setShowSearchResults']),
    ...mapActions('AngularStore', ['setIsOverlayDisplay']),
    ...mapActions('NotificationCenterStore', ['initNotificationCenterBadge']),
    handleLogoClick() {
      this.activateDefaultItem({ path: this.defaultRoute });
      handleNavigation({ path: this.defaultRoute });
    },
    navItemClick(item) {
      this.executeItemClick(item);
    },
    executeItemClick(item) {
      this.storeHandleItemClick({ item });
      if (hasAction(item)) {
        executeAction(item.action);
      }
    },
    handleFatalError() {
      this.$router.push('/error');
    },
    replaySidebarState() {
      outgoingPostMessageUtils.post('toggle-sidebar-visibility', {
        showSidebar: this.showSidebar,
      });
    },
    routeChange(to, from, next) {
      if (!this.navigationTree || this.navigationTree.length === 0) {
        next();
        return;
      }
      const currentPath = to?.path || this.$route.path;
      this.setActiveItemFromRoute({ path: currentPath });
      next();
    },
  },
};
</script>

<style lang="scss" scoped>
.master-top-bar {
  display: flex;
  align-items: center;
  gap: var(--size-value4);
  padding-inline: var(--size-value4);
  background-color: var(--primary-color, var(--white));
  color: var(--v-text-base);
  border-bottom: 1px solid var(--neutral-lighten-1);
  min-height: 56px;

  &--loading {
    justify-content: space-between;
  }

  &--search-active {
    gap: 0;
  }

  &__logo {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    cursor: pointer;
    height: 40px;
    max-width: 200px;
    overflow: hidden;
    opacity: 1;
    transition: max-width 0.3s ease, opacity 0.3s ease, margin 0.3s ease;

    &--collapsed {
      max-width: 0;
    }

    &:focus {
      outline: 2px solid var(--primary-color);
      outline-offset: 2px;
    }

    ::v-deep .VcImage {
      height: 100%;
      min-width: 0;

      .image {
        height: 100%;
        width: auto;
        object-fit: contain;
      }
    }
  }

  &__navigation {
    flex: 1 1 0;
    min-width: 0;
    display: flex;
    align-items: center;
    overflow: hidden;
    transition: min-width 0.5s ease, opacity 0.3s ease;

    &--collapsed {
      flex: 0 0 0;
      min-width: 0;
      opacity: 0;
      pointer-events: none;
    }
  }

  &__actions {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    gap: var(--size-value2);

    &--search-active {
      flex: 1 1 0;
      min-width: 0;
    }
  }
}
</style>
